// 主入口: 游戏循环 (人机对战)
// 开局随机分配红/黑给玩家, AI 执另一色. 红先行. 每步后检测终局.

import { startState } from './game/fen';
import { GameState, Move, Square } from './game/types';
import { applyMove, gameResult, isInCheck, isCheckmate } from './game/rules';
import { moveToICCS, moveToChinese } from './game/notation';
import { computeHash } from './ai/zobrist';
import { classifyRepetition, PositionOccurrence } from './game/repetition';
import { MatchGate } from './game/matchGate';
import {
  DEFAULT_LAYOUT, redraw,
} from './ui/renderer';
import { attachInput } from './ui/input';
import { createWorkerBot } from './ai/workerBot';
import type { WorkerBackedBot } from './ai/workerBot';
import type { Level } from './ai/levels';
import { AiPanelController } from './ui/aiPanel';
import { CredentialDialog } from './ui/credentialDialog';
import { MemoryCredentialVault } from './security/credentialVault';
import {
  describeRemoteBrainFailure,
  gatewayClientFailureCause,
  remoteBrainFailureCode,
  RemoteModelBrain,
} from './brain/remoteBrain';
import type { BrainSelection } from './brain/types';
import { playAudioSafely } from './ui/audio';
import { classifyLandingEffect, PieceMotionState } from './ui/pieceMotion';
// Vite 以 URL 形式处理静态资源 (小文件将内联为 data URI).
// 音效: 落子 / 将军 / 绝杀(将死/困毙/长将判负的对手胜) / 和棋.
import luoziUrl from '../assets/luozi.mp3';
import jiangjunUrl from '../assets/jiangjun.m4a';
import jueshaUrl from '../assets/juesha.m4a';
import heqiUrl from '../assets/heqi.m4a';
// 终局弹框顶图: 用户可替换 assets/victory.svg 为任意图片 (png/jpg/svg/webp).
// 替换后改下方 import 后缀与文件名以匹配.
import victoryImgUrl from '../assets/victory.png';

const canvas = document.getElementById('board') as HTMLCanvasElement;
const ctx = canvas.getContext('2d')!;
const statusEl = document.getElementById('status') as HTMLSpanElement;
const startGameBtn = document.getElementById('start-game') as HTMLButtonElement;
const newGameBtn = document.getElementById('new-game') as HTMLButtonElement;
const undoBtn = document.getElementById('undo') as HTMLButtonElement;
const resignBtn = document.getElementById('resign') as HTMLButtonElement;
const movesList = document.getElementById('moves-list') as HTMLOListElement;

// 终局弹框 DOM
const endDialog = document.getElementById('end-dialog') as HTMLDivElement;
const endImage = document.getElementById('end-image') as HTMLImageElement;
const endText = document.getElementById('end-text') as HTMLParagraphElement;
const endNewGameBtn = document.getElementById('end-new-game') as HTMLButtonElement;
const endCloseBtn = document.getElementById('end-close') as HTMLButtonElement;
endImage.src = victoryImgUrl; // 顶图: 默认占位 SVG, 用户可替换

function hideEndDialog(): void {
  endDialog.classList.add('hidden');
}

// 音效: 每个场景一个 Audio 节点, 复用以避免频繁创建对象.
type SfxName = 'luozi' | 'jiangjun' | 'juesha' | 'heqi';
const sfxNodes: Record<SfxName, HTMLAudioElement> = {
  luozi: new Audio(luoziUrl),
  jiangjun: new Audio(jiangjunUrl),
  juesha: new Audio(jueshaUrl),
  heqi: new Audio(heqiUrl),
};
for (const n of Object.values(sfxNodes)) n.preload = 'auto';

function playSfx(name: SfxName): void {
  void playAudioSafely(sfxNodes[name]);
}

// 配置 Canvas 实际像素尺寸 (CSS 由 layout 控制)
canvas.width = DEFAULT_LAYOUT.width;
canvas.height = DEFAULT_LAYOUT.height;

interface HistoryEntry {
  state: GameState;
  move: Move | null;
  notation: string | null; // 中文纵线记谱 (e.g. "炮二平五")
  occurrence: PositionOccurrence;
}
let state: GameState = startState();
let history: HistoryEntry[] = [{
  state,
  move: null,
  notation: null,
  occurrence: { hash: computeHash(state), mover: null, gaveCheck: false },
}];
let selected: Square | null = null;
let legalTargets: Square[] = [];
let drag: { square: Square; piece: import('./game/types').Piece; x: number; y: number } | null = null;
let lastMove: { from: Square; to: Square } | null = null;
let botThinking = false;
let gameGeneration = 0;
const matchGate = new MatchGate();
// 元游戏终局标志: 认输/长将/三循环等非棋盘胜负. winner='draw' 表示和棋.
// 为 null 时按棋盘 gameResult 判定
let gameOver: { winner: 'r' | 'b' | 'draw'; reason: string } | null = null;
// 点击开始对局时随机执方；准备阶段暂用红方视角展示棋盘.
let userColor: 'r' | 'b' = 'r';
let activeSelection: BrainSelection = { kind: 'local', level: 'intermediate' };
let remoteAbortController: AbortController | null = null;
let thinkingTimerInterval: ReturnType<typeof setInterval> | null = null;
let visualFrameId: number | null = null;
const pieceMotion = new PieceMotionState(
  () => performance.now(),
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
);

const localBots = {
  beginner: createWorkerBot('beginner'),
  intermediate: createWorkerBot('intermediate'),
  advanced: createWorkerBot('advanced'),
  nascentSoul: createWorkerBot('nascentSoul'),
  spiritSevering: createWorkerBot('spiritSevering'),
  ascendant: createWorkerBot('ascendant'),
} satisfies Record<Level, WorkerBackedBot>;

function cancelLocalBots(): void {
  for (const bot of Object.values(localBots)) bot.cancel();
}

function cancelBrains(): void {
  cancelLocalBots();
  remoteAbortController?.abort();
  remoteAbortController = null;
}

function clearThinkingTimerInterval(): void {
  if (thinkingTimerInterval === null) return;
  clearInterval(thinkingTimerInterval);
  thinkingTimerInterval = null;
}

function startThinkingTimer(): void {
  clearThinkingTimerInterval();
  aiPanel.startThinkingTimer();
  thinkingTimerInterval = setInterval(() => aiPanel.refreshThinkingTimer(), 33);
}

function completeThinkingTimer(): number {
  clearThinkingTimerInterval();
  return aiPanel.completeThinkingTimer();
}

function cancelThinkingTimer(): void {
  clearThinkingTimerInterval();
  aiPanel.cancelThinkingTimer();
}

function resetThinkingTimer(): void {
  clearThinkingTimerInterval();
  aiPanel.resetThinkingTimer();
}

const credentialVault = new MemoryCredentialVault();
let credentialDialog: CredentialDialog;
const aiPanel = new AiPanelController({
  onSelectionChange(selection, wasLocked) {
    activeSelection = selection;
    if (wasLocked) {
      startNewGame();
    } else if (matchGate.started) {
      gameGeneration += 1;
      cancelBrains();
      cancelThinkingTimer();
      botThinking = false;
      void aiMoveIfTurn();
    }
    return true;
  },
  onOpenCredentials(provider) {
    credentialDialog.open(provider);
  },
});
credentialDialog = new CredentialDialog(credentialVault, () => void aiMoveIfTurn());

function aiColor(): 'r' | 'b' {
  return userColor === 'r' ? 'b' : 'r';
}

function render(): void {
  const motion = pieceMotion.snapshot();
  redraw(ctx, DEFAULT_LAYOUT, state, {
    selected,
    legalTargets,
    lastMove,
    drag,
    userColor,
    motion,
  });
  if (motion.animating) requestVisualFrame();
}

function requestVisualFrame(): void {
  if (visualFrameId !== null) return;
  visualFrameId = requestAnimationFrame(() => {
    visualFrameId = null;
    render();
  });
}

function cancelVisualFrame(): void {
  if (visualFrameId === null) return;
  cancelAnimationFrame(visualFrameId);
  visualFrameId = null;
}

// 根据当前局面 (刚走完一手后的 state) 选择落子音效.
// 优先级: 元游戏终局(gameOver 已设置) → 棋盘终局(gameResult) → 将军 → 普通落子.
// 调用前提: state 已是 applyMove 后的新状态; gameOver 已被重复局面逻辑更新.
function pickSfx(): SfxName {
  // 元游戏终局: 三循环判和 → 和棋; 长将作负 → 绝杀 (本手将军致对方胜, 终结音)
  if (gameOver) {
    return gameOver.winner === 'draw' ? 'heqi' : 'juesha';
  }
  // 棋盘终局: 将死/困毙 (= 走子方胜) → 绝杀; 60 回合/不足子力和棋 → 和棋
  const result = gameResult(state);
  if (result === 'draw') return 'heqi';
  if (result !== 'ongoing') return 'juesha';
  // 未终局: 对手仍被将 (有解) → 将军
  if (isInCheck(state, state.turn)) return 'jiangjun';
  return 'luozi';
}

function pushMove(move: Move): void {
  // 在 applyMove 前读取起始格棋子, 用于中文记谱 (棋子名 + 颜色)
  const mover = state.board[move.from];
  const notation = mover
    ? moveToChinese(move, mover.type, mover.color)
    : moveToICCS(move); // 防御: 异常走法 (理论上不会发生)
  const moverColor = state.turn;
  state = applyMove(state, move);
  lastMove = { from: move.from, to: move.to };
  const occurrence: PositionOccurrence = {
    hash: computeHash(state),
    mover: moverColor,
    gaveCheck: isInCheck(state, state.turn),
  };
  history.push({ state, move, notation, occurrence });
  if (matchGate.shouldLockBrain(history.length)) aiPanel.lock();

  const repetition = classifyRepetition(
    history.map(entry => entry.occurrence),
    occurrence.hash,
  );
  if (repetition.kind === 'perpetual-check-loss' && !gameOver) {
    gameOver = {
      winner: repetition.loser === 'r' ? 'b' : 'r',
      reason: '长将作负',
    };
  } else if (repetition.kind === 'draw' && !gameOver) {
    gameOver = { winner: 'draw', reason: '三循环判和' };
  }
  pieceMotion.land(move.to, classifyLandingEffect({
    terminal: gameOver !== null || gameResult(state) !== 'ongoing',
    givesCheck: occurrence.gaveCheck,
    capture: Boolean(move.captured),
  }));
  addMoveListItem(notation, mover ? mover.color : state.turn === 'b' ? 'r' : 'b');
  render();
  playSfx(pickSfx());
  checkGameOver();
}

function addMoveListItem(notation: string, moverColor: 'r' | 'b'): void {
  const li = document.createElement('li');
  li.textContent = `${moverColor === 'r' ? '红' : '黑'} ${notation}`;
  movesList.appendChild(li);
  movesList.scrollTop = movesList.scrollHeight;
}

// 当前回合的状态文案 (玩家色用"你"标注)
function statusText(): string {
  if (!matchGate.started) return '请选择 AI 棋手，然后开始对局';
  // 元游戏终局 (认输/长将/三循环) 优先
  if (gameOver) {
    if (gameOver.winner === 'draw') {
      return `游戏结束 — 和棋!(${gameOver.reason})`;
    }
    const winnerName = gameOver.winner === 'r' ? '红方' : '黑方';
    return `游戏结束 — ${winnerName}胜!(${gameOver.reason})`;
  }
  const result = gameResult(state);
  if (result !== 'ongoing') {
    let msg: string;
    if (result === 'red-win') msg = '红方胜!';
    else if (result === 'black-win') msg = '黑方胜!';
    else msg = '和棋!';
    return `游戏结束 — ${msg}`;
  }
  if (state.turn === userColor) {
    return userColor === 'r' ? '红方走 (你)' : '黑方走 (你)';
  }
  return aiColor() === 'r' ? '红方思考中...' : '黑方思考中...';
}

function checkGameOver(): void {
  statusEl.textContent = statusText();
  if (gameResult(state) !== 'ongoing') botThinking = false;
  maybeShowEndDialog();
}

// 检测当前是否进入"有胜方"的终局, 若是则弹出终局对话框.
// 和棋(draw) 仅在状态栏展示, 不弹框. 已显示则不重复触发.
function maybeShowEndDialog(): void {
  if (!endDialog.classList.contains('hidden')) return;

  const winnerName = (w: 'r' | 'b') => (w === 'r' ? '红方' : '黑方');
  const loserName = (w: 'r' | 'b') => (w === 'r' ? '黑方' : '红方');

  let winner: 'r' | 'b' | null = null;
  let loserPhrase: string | null = null;

  // 优先看元游戏终局 (认输/长将作负). 三循环 winner='draw' 不弹.
  if (gameOver && gameOver.winner !== 'draw') {
    const w = gameOver.winner;
    winner = w;
    if (gameOver.reason === '玩家认输') {
      loserPhrase = `${loserName(w)}认输`;
    } else if (gameOver.reason === '长将作负') {
      loserPhrase = `${loserName(w)}长将判负`;
    } else {
      loserPhrase = `${loserName(w)}${gameOver.reason}`;
    }
  } else if (!gameOver) {
    // 棋盘终局: 将死/困毙 → 有胜方; 和棋 → 不弹
    const result = gameResult(state);
    if (result === 'red-win' || result === 'black-win') {
      const w: 'r' | 'b' = result === 'red-win' ? 'r' : 'b';
      winner = w;
      loserPhrase = isCheckmate(state)
        ? `${loserName(w)}被绝杀`
        : `${loserName(w)}被困毙`;
    }
  }

  if (winner && loserPhrase) {
    const isPlayerWin = winner === userColor;
    const prefix = isPlayerWin ? '你胜!' : '你负!';
    endText.textContent = `${prefix} ${winnerName(winner)}胜 — ${loserPhrase}`;
    endDialog.classList.remove('hidden');
  }
}

async function aiMoveIfTurn(): Promise<void> {
  if (!matchGate.canMove()) return;
  if (botThinking) return;
  if (gameOver) return;
  if (state.turn !== aiColor()) return;
  const result = gameResult(state);
  if (result !== 'ongoing') return;
  const apiKey = activeSelection.kind === 'remote'
    ? credentialVault.get(activeSelection.model.provider)
    : undefined;
  if (activeSelection.kind === 'remote' && !apiKey) {
    aiPanel.showCredentialRequired();
    credentialDialog.open(activeSelection.model.provider);
    return;
  }
  botThinking = true;
  const generation = gameGeneration;
  const searchedState = state;
  statusEl.textContent = aiColor() === 'r' ? '红方思考中...' : '黑方思考中...';
  aiPanel.showThinking();
  startThinkingTimer();
  render();
  // 让 UI 有时间重绘
  await new Promise(r => setTimeout(r, 30));
  let move: Move | null = null;
  let reason: string | undefined;
  let latencyMs = 0;
  let fellBack = false;
  let fallbackDetail: string | undefined;
  try {
    if (activeSelection.kind === 'local') {
      move = await localBots[activeSelection.level].chooseMove(
        state,
        history.map(entry => entry.occurrence),
      );
    } else {
      remoteAbortController = new AbortController();
      const decision = await new RemoteModelBrain(activeSelection.model).chooseMove({
        state,
        recentMoves: history.slice(1).flatMap(entry => entry.move ? [moveToICCS(entry.move)] : []),
        apiKey: apiKey!,
        signal: remoteAbortController.signal,
      });
      move = decision.move;
      reason = decision.reason;
    }
  } catch (error) {
    if (generation !== gameGeneration || state !== searchedState) return;
    fellBack = true;
    console.warn('[model-brain]', {
      provider: activeSelection.kind === 'remote' ? activeSelection.model.provider : 'local',
      model: activeSelection.kind === 'remote' ? activeSelection.model.model : activeSelection.level,
      error: remoteBrainFailureCode(error),
      cause: gatewayClientFailureCause(error),
      origin: window.location.origin,
    });
    fallbackDetail = describeRemoteBrainFailure(error);
    move = await localBots.intermediate.chooseMove(
      state,
      history.map(entry => entry.occurrence),
    );
  } finally {
    remoteAbortController = null;
  }
  if (generation !== gameGeneration || state !== searchedState) return;
  latencyMs = completeThinkingTimer();
  botThinking = false;
  if (move) {
    if (fellBack) aiPanel.showFallback(moveToICCS(move), latencyMs, fallbackDetail);
    else aiPanel.showResult(moveToICCS(move), latencyMs, reason);
    pushMove(move);
    if (!gameOver && gameResult(state) === 'ongoing' && state.turn === aiColor()) {
      // 轮到 AI 继续 (人机对战中不会发生, 但防御性处理)
      aiMoveIfTurn();
    }
  }
}

// 指针输入
attachInput(canvas, DEFAULT_LAYOUT, () => state, () => userColor, {
  onPickSelected: (sq, targets) => {
    if (!matchGate.canMove()) return;
    if (gameOver) return;
    selected = sq;
    legalTargets = targets;
    pieceMotion.select(sq);
    render();
  },
  onDragUpdate: (d) => {
    if (!matchGate.canMove()) return;
    drag = d;
    requestVisualFrame();
  },
  onMove: (move) => {
    if (!matchGate.canMove()) return;
    if (gameOver) return;
    pushMove(move);
    if (!gameOver && gameResult(state) === 'ongoing') {
      aiMoveIfTurn();
    }
  },
});

// 新局先回到准备阶段，留出选择棋手和配置密钥的时间.
function startNewGame(): void {
  gameGeneration += 1;
  cancelBrains();
  resetThinkingTimer();
  cancelVisualFrame();
  pieceMotion.reset();
  state = startState();
  history = [{
    state,
    move: null,
    notation: null,
    occurrence: { hash: computeHash(state), mover: null, gaveCheck: false },
  }];
  selected = null;
  legalTargets = [];
  drag = null;
  lastMove = null;
  movesList.innerHTML = '';
  botThinking = false;
  gameOver = null; // 清除认输/元游戏终局
  matchGate.reset();
  aiPanel.unlock();
  aiPanel.showIdle();
  hideEndDialog(); // 关闭终局弹框 (若开着)
  userColor = 'r';
  startGameBtn.disabled = false;
  startGameBtn.textContent = '开始对局';
  statusEl.textContent = statusText();
  render();
}

// 控件
startGameBtn.addEventListener('click', () => {
  if (!matchGate.start()) return;
  resetThinkingTimer();
  userColor = Math.random() < 0.5 ? 'r' : 'b';
  startGameBtn.disabled = true;
  startGameBtn.textContent = '对局中';
  statusEl.textContent = statusText();
  render();
  void aiMoveIfTurn();
});
newGameBtn.addEventListener('click', startNewGame);

// 终局弹框按钮: 新局 = 关闭弹框 + 重启; 关闭 = 仅关弹框, 棋盘保留以便复盘.
endNewGameBtn.addEventListener('click', startNewGame);
endCloseBtn.addEventListener('click', hideEndDialog);

// 认输: 当前玩家点击 → 对方胜. 二次确认避免误点.
resignBtn.addEventListener('click', () => {
  if (!matchGate.canMove()) return;
  if (gameOver) return; // 已结束
  if (botThinking) return; // AI 思考中禁用
  if (gameResult(state) !== 'ongoing') return; // 棋盘已分胜负
  if (!confirm('确认认输?本局将判对方胜。')) return;
  gameOver = {
    winner: aiColor(), // 玩家认输 → AI 胜
    reason: '玩家认输',
  };
  botThinking = false;
  statusEl.textContent = statusText();
  render();
  maybeShowEndDialog(); // 弹出胜负对话框
});

undoBtn.addEventListener('click', () => {
  if (!matchGate.canMove()) return;
  if (botThinking) return;
  if (gameOver) return; // 已结束 (含认输), 禁用悔棋. 需先开新局.
  // 回退到上一个玩家走子前的状态
  // history: [start, plie1, plie2, ...] (红先行, 奇数 plie 为红, 偶数为黑)
  // 持续回退直到轮到玩家色
  while (history.length > 1) {
    history.pop();
    state = history[history.length - 1].state;
    if (state.turn === userColor && history.length > 1) break;
  }
  if (history.length === 1) {
    // 回到起手
    state = startState();
    lastMove = null;
  } else {
    const last = history[history.length - 1].move;
    lastMove = last ? { from: last.from, to: last.to } : null;
  }
  selected = null;
  legalTargets = [];
  drag = null;
  cancelVisualFrame();
  pieceMotion.reset();
  // 重建 moves list (使用 history 中已存的中文记谱)
  movesList.innerHTML = '';
  for (let i = 1; i < history.length; i++) {
    const entry = history[i];
    if (!entry.move || !entry.notation) continue;
    const moverColor: 'r' | 'b' = (i % 2 === 1) ? 'r' : 'b';
    const li = document.createElement('li');
    li.textContent = `${moverColor === 'r' ? '红' : '黑'} ${entry.notation}`;
    movesList.appendChild(li);
  }
  if (matchGate.shouldLockBrain(history.length)) aiPanel.lock();
  else aiPanel.unlock();
  statusEl.textContent = statusText();
  render();
});

window.addEventListener('beforeunload', () => {
  remoteAbortController?.abort();
  cancelThinkingTimer();
  cancelVisualFrame();
  pieceMotion.reset();
  for (const bot of Object.values(localBots)) bot.dispose();
});

// 初次渲染
render();
statusEl.textContent = statusText();
